<template>
  <v-container fluid>
    <v-row class="d-flex h-100 flex-column">
      <progress-bar v-if="isLoading" :message="$t('general.loading')" />
      <v-row v-else>
        <v-col class="px-0" :class="colClass">
          <ReportingMenu />
        </v-col>
        <v-col>
          <div
            v-if="isValidSelection"
            id="reports-section"
            class="d-flex flex-column h-100 position-relative"
          >
            <v-overlay
              :model-value="reportingStore.isReportDataStale"
              attach
              contained
              persistent
              class="align-center justify-center text-center"
            >
              <div class="pa-4 bg-white rounded">
                <h3>{{ $t('reportingPage.selectionsChangedMessage') }}</h3>
              </div>
            </v-overlay>
            <ReportingTabs />
            <router-view />
          </div>
        </v-col>
      </v-row>
    </v-row>
  </v-container>
</template>

<script>
import { get, size, some, map, isEqual, filter, concat, isEmpty, isNull } from 'lodash';
import reportingFilters from '@enums/reporting-filters';
import { useWorkpackagesStore } from '@/stores/workpackages';
import { useBundlesStore } from '@/stores/bundles';
import { useReportingStore } from '@/stores/reporting';
import { useWorkpackageScenarioSelectStore } from '@/stores/workpackage-scenario-select';
import { useInternationalization } from '@/composables/use-internationalization';
import { useFeature } from '@/composables/use-feature';

export default {
  async beforeRouteUpdate(to, from, next) {
    const selections = {};

    if (this.bundlesEnabled) {
      const bundleIds = concat([], get(to, 'query.bundleIds', []));
      const previousBundleIds = concat([], get(from, 'query.bundleIds', []));

      if (!isEqual(bundleIds, previousBundleIds)) {
        selections.bundleIds = bundleIds;
      }
    }
    const workpackageIds = concat([], get(to, 'query.workpackageIds', []));
    const previousWorkpackageIds = concat([], get(from, 'query.workpackageIds', []));

    if (!isEqual(workpackageIds, previousWorkpackageIds)) {
      selections.workpackageIds = workpackageIds;
    }
    // when selected bundles / workpackages are the same, we assume the user has changed filters,
    // which don't trigger more updates
    if (isEmpty(selections)) return next();

    // if there are any new selections, we need to load more data
    // this may not be the case if the user unselected everything
    const shouldLoadData = some(selections, size);

    // on selection change, reset all report section data
    // we will only load more data if the user has selected new workpackages / bundles
    this.resetSelected(selections, shouldLoadData);

    return next();
  },

  beforeRouteLeave(to, from, next) {
    const selections = { workpackageIds: [] };
    if (this.bundlesEnabled) selections.bundleIds = [];
    this.resetSelected(selections);
    return next();
  },

  setup() {
    const workpackagesStore = useWorkpackagesStore();
    const bundlesStore = useBundlesStore();
    const reportingStore = useReportingStore();
    const workpackageScenarioSelectStore = useWorkpackageScenarioSelectStore();
    const { t } = useInternationalization('reporting');
    const { bundlesEnabled } = useFeature();

    return {
      t,
      workpackagesStore,
      bundlesStore,
      reportingStore,
      workpackageScenarioSelectStore,
      bundlesEnabled,
    };
  },

  data() {
    return { noBundle: { _id: null, name: this.t('reportingPage.noBundle') } };
  },

  computed: {
    isLoading() {
      return this.reportingStore.isLoading;
    },

    workpackageIds() {
      const workpackageIds = this.$route.query.workpackageIds || [];
      return concat([], workpackageIds);
    },

    bundleIds() {
      const bundleIds = this.$route.query.bundleIds;
      if (!bundleIds) return isNull(bundleIds) ? [null] : [];
      return concat([], bundleIds);
    },

    isValidSelection() {
      if (!this.bundlesEnabled) return this.isValidWorkpackage;
      return this.isValidBundles || this.isValidWorkpackage;
    },

    isValidBundles() {
      if (!size(this.workpackageScenarioSelectStore.selectedBundles)) return;
      const bundleIds = map(this.workpackageScenarioSelectStore.selectedBundles, '_id');
      const bundleHasWorkpackages = some(
        bundleIds,
        id => !!size(this.workpackagesStore.workpackagesByBundle[id])
      );
      return this.workpackageScenarioSelectStore.selectedBundles && bundleHasWorkpackages;
    },

    isValidWorkpackage() {
      if (!size(this.workpackageScenarioSelectStore.selectedWorkpackages)) return;
      const wpIds = map(this.workpackageScenarioSelectStore.selectedWorkpackages, '_id');
      const workpackagesHasScenarios = some(
        wpIds,
        id => !!size(this.workpackageScenarioSelectStore.scenariosByWorkpackage[id])
      );
      return this.workpackageScenarioSelectStore.selectedWorkpackages && workpackagesHasScenarios;
    },

    currentQueryValues() {
      return this.$route.query || {};
    },

    colClass() {
      return this.reportingStore.isSidebarShown ? 'v-col-5' : 'v-col-auto';
    },
  },

  created() {
    return this.loadAllWorkpackagesData();
  },

  methods: {
    async loadAllWorkpackagesData() {
      // even if there are workpackages in state, we need to refetch it to pull this extra info
      await this.workpackagesStore.fetchWorkpackages({
        shouldGetTemplates: false,
        pickExtra: ['fillInSelection'],
      });

      const selections = {};

      if (this.bundlesEnabled) {
        await this.bundlesStore.fetchBundles({ pick: ['_id', 'name', 'creationDate'] });

        if (!size(this.workpackageScenarioSelectStore.selectedBundles) && size(this.bundleIds)) {
          selections.bundleIds = this.bundleIds;
        }
      }

      if (
        !size(this.workpackageScenarioSelectStore.selectedWorkpackages) &&
        size(this.workpackageIds)
      ) {
        // when loading for the first time (copy & paste full link), we take whatever is on the url is
        selections.workpackageIds = this.workpackageIds;
      }

      this.resetSelected(selections, true);
      await this.workpackageScenarioSelectStore.fetchWorkpackageScenarios();
    },

    resetSelected(selections, loadWPData = false) {
      const { bundleIds, workpackageIds } = selections;

      if (bundleIds) this.resetBundlesSelected(bundleIds);
      if (workpackageIds) this.resetWorkpackagesSelected(workpackageIds);

      if (!loadWPData) return;
      return this.resetWPData();
    },

    resetBundlesSelected(bundleIds) {
      const bIds = new Set(bundleIds);
      const bundles = bIds.size
        ? filter([...this.bundlesStore.bundles, this.noBundle], b => bIds.has(b._id))
        : [];
      this.workpackageScenarioSelectStore.selectedBundles = bundles;
    },

    resetWorkpackagesSelected(workpackageIds) {
      const wpIds = new Set(workpackageIds);
      const workpackages = wpIds.size
        ? filter(this.workpackagesStore.workpackages, w => wpIds.has(w._id))
        : [];
      this.workpackageScenarioSelectStore.selectedWorkpackages = workpackages;
    },

    // this method resets all information loaded (scenarios, canvases, checkpoints, etc) with newly selected bundle / workpackage data
    async resetWPData() {
      this.reportingStore.isLoading = true;
      this.reportingStore.setReportDataStale(false);
      // manually pre-set the filters which directly interfere with the shown contents, otherwise, the fetch call
      // will return invalid aggregated data; then we can check if the new baseline & comparison are valid
      this.reportingStore.resetFilters({ baseline: [], comparison: [], comparison2: [] });
      // get last items first, then fetch scenarios
      await this.reportingStore.loadWorkpackagesData();

      // after reloading all data, check the query data
      const baseline = this.getValidSelections(reportingFilters.baseline);
      const comparison = this.getValidSelections(reportingFilters.comparison);
      const comparison2 = this.getValidSelections(reportingFilters.comparison2);
      // reset charts required filters so overview page loads with the correct info
      const nextAttributeValue = this.reportingStore.getFirstCommonAttribute({
        baselineIds: baseline,
        comparisonIds: comparison,
        comparison2Ids: comparison2,
      });
      this.reportingStore.attributeValue = nextAttributeValue;
      this.reportingStore.metricValue = this.getUrlQueryString('metricValue');
      await this.reportingStore.resetFilters({ baseline, comparison, comparison2 });

      const wasBundlesChanged = this.bundlesEnabled
        ? !isEqual(this.workpackageScenarioSelectStore.selectedBundles, this.bundleIds)
        : false;
      const wasWorkpackagesChanged = !isEqual(
        this.workpackageScenarioSelectStore.selectedWorkpackages,
        this.workpackageIds
      );
      const wasBaselineModified = !isEqual(
        baseline,
        concat([], this.getUrlQueryString(reportingFilters.baseline))
      );
      const wasComparisonModified = !isEqual(
        comparison,
        concat([], this.getUrlQueryString(reportingFilters.comparison))
      );
      const wasComparison2Modified = !isEqual(
        comparison2,
        concat([], this.getUrlQueryString(reportingFilters.comparison2))
      );
      if (
        wasBaselineModified ||
        wasComparisonModified ||
        wasComparison2Modified ||
        wasBundlesChanged ||
        wasWorkpackagesChanged
      ) {
        const newParams = { ...this.currentQueryValues, baseline };
        if (wasComparisonModified) newParams.comparison = comparison;
        if (wasComparison2Modified) newParams.comparison2 = comparison2;
        if (wasBundlesChanged) newParams.bundleIds = this.bundleIds;
        if (wasWorkpackagesChanged) newParams.workpackageIds = this.workpackageIds;
        this.redirectUserToURL(newParams);
      }

      this.reportingStore.isLoading = false;
    },

    getUrlQueryString(field) {
      return this.$route.query[field] || null;
    },

    getValidSelections(dataLevel) {
      const selection = this.reportingStore.getSelectionFromId(this.getUrlQueryString(dataLevel));
      return map(selection, '_id');
    },

    redirectUserToURL(query, redirectToOverview = false) {
      // avoid adding unnecessary key to URL
      if (!query.baseline) delete query.baseline;
      if (!query.comparison) delete query.comparison;
      if (!query.comparison2) delete query.comparison2;
      if (!query.attributeValue) delete query.attributeValue;

      // updates URL to include new query params
      this.$router.push({
        path: redirectToOverview ? '/reporting-main' : this.$route.path, // keep on same path
        query,
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@style/base/_variables.scss';
</style>
